// SPDX-License-Identifier: GPL-3.0-or-later
import { sanitizeRoomName } from '@ffb/shared';
import {
  ROOM_NAME_MAX,
  ROOM_NAME_MIN,
  ROOM_NAME_PATTERN,
  USER_NAME_MAX,
  USER_NAME_MIN,
  type LobbyInput,
} from '../models/Room.js';

export interface LobbyJoinPayload {
  userName: string;
  roomName: string;
}

export interface LobbyViewModelOptions {
  onJoin: (payload: LobbyJoinPayload) => void;
}

// MVVM: ViewModel лобби. Состояние — $state, бизнес-правила валидации здесь.
// Тестируется без DOM — любой компонент связывается с ним через привязку и колбэк onJoin.
export class LobbyViewModel {
  userName = $state('');
  roomName = $state('');
  error = $state<string | null>(null);

  private readonly onJoin: (payload: LobbyJoinPayload) => void;

  constructor({ onJoin }: LobbyViewModelOptions) {
    this.onJoin = onJoin;
  }

  setUserName(value: string): void {
    this.userName = value;
    if (this.error) this.error = null;
  }

  setRoomName(value: string): void {
    this.roomName = value;
    if (this.error) this.error = null;
  }

  validate(): string | null {
    const user = this.userName.trim();
    if (user.length < USER_NAME_MIN) return 'Введите своё имя';
    if (user.length > USER_NAME_MAX) return `Имя не длиннее ${USER_NAME_MAX} символов`;

    const rawRoom = this.roomName.trim();
    if (rawRoom.length === 0) return 'Введите название комнаты';
    const room = sanitizeRoomName(rawRoom);
    if (room.length < ROOM_NAME_MIN)
      return `Название комнаты должно быть не короче ${ROOM_NAME_MIN} символов`;
    if (room.length > ROOM_NAME_MAX)
      return `Название комнаты не длиннее ${ROOM_NAME_MAX} символов`;
    if (!ROOM_NAME_PATTERN.test(room)) return 'Только латиница, цифры и дефис';
    // Из чисто спецсимвольной строки санация даёт «----» — такое не принимаем.
    if (!/[a-z0-9]/.test(room)) return 'Название должно содержать хотя бы одну букву или цифру';
    return null;
  }

  submit(): void {
    const err = this.validate();
    if (err) {
      this.error = err;
      return;
    }
    this.error = null;
    this.onJoin({
      userName: this.userName.trim(),
      roomName: sanitizeRoomName(this.roomName.trim()),
    });
  }

  reset(input?: Partial<LobbyInput>): void {
    this.userName = input?.userName ?? '';
    this.roomName = input?.roomName ?? '';
    this.error = null;
  }
}
